/* eslint-disable max-len */
import React from 'react'
import Button from 'react-bootstrap/Button'
import { RelatedCompanyModel } from '../../../../../models/RelatedCompanyModel'
import {
  TextField, BooleanRadioButtonField, yesNoDataSource, TextSelectField, validateFields, AddressField, isAbnFormatValid,
} from '../../../../../shared/fields'
import { TableColumn, ISection } from '../../../../../shared/table'
import { ShowIf } from '../../../../../shared/fields/ShowIf'
import { ISupportSpinner } from '../../../../../shared/Ajax'
import { Backdrop } from '../../../../../shared/Backdrop'
import { ApplicationModel } from '../../../../../models/ApplicationModel'
import { Option } from '../../../../../models/Option'
import { CompanyRelationship, formatDigits } from '../../../../../shared/Utility'

import { AbnOrAnyField } from '../../../../../shared/fields/AbnOrAnyField'
import { AbnController } from '../../../../../controllers/AbnController'

export const relatedCompanyColumns: TableColumn<RelatedCompanyModel>[] = [
  { name: 'Company name', content: (d) => d.companyName },
  { name: 'Business activity', content: (d) => d.businessActivity },
  { name: 'ABN / Registration', content: (d) => d.businessRegistrationNumberOrABN && d.businessRegistrationNumberOrABN.length === 11 ? formatDigits(d.businessRegistrationNumberOrABN) : d.businessRegistrationNumberOrABN },
  { name: 'Applying for EMDG', content: (d) => (d.isApplyingForEMDG ? 'Yes' : 'No') },
]

export interface RelatedCompanyDetailsProps {
    id: string
    model: RelatedCompanyModel
    onChange: { (v: RelatedCompanyModel, close: boolean): any }
    onClose: { (): any }
    onRemove: { (): any }
    importing: boolean
    preview?: boolean
    application: ApplicationModel
    addressCountries: Option<string>[]
}

export interface RelatedCompanyDetailsState {
    model: RelatedCompanyModel
    loading: boolean
}

export class RelatedCompanyDetails extends React.Component<RelatedCompanyDetailsProps, RelatedCompanyDetailsState> implements ISection, ISupportSpinner {
  companyName: TextField
  businessActivity: TextField
  businessRegistrationNumberOrABN : TextField
  // acn: TextField
  registeredAddress: AddressField
  isApplyingForEMDG: BooleanRadioButtonField
  relationship: TextSelectField
  otherRelatedCompany: TextField
  abnAjax: AbnController

  constructor(props: RelatedCompanyDetailsProps) {
    super(props)

    const model: RelatedCompanyModel = this.props.model ? { ...this.props.model } : {
      relatedCompanyID: 0,
      applicationID: this.props.application.applicationID,
      companyName: '',
      businessActivity: '',
      rowState: 1,
      businessRegistrationNumberOrABN: '',
      acn: '',
      registeredAddress: null,
      isApplyingForEMDG: false,
      relationship: '',
      invalid: false,
    }

    this.abnAjax = new AbnController()

    this.state = {
      model,
      loading: false,
    }
  }

  toggleSpinner(on: boolean, callback: { (): any }): void {
    this.setState({ loading: on }, callback)
  }

  validate = (callback: { (res: boolean): any }) => {
    validateFields((res) => this.onChange({ invalid: !res }, () => callback(res)),
      this.companyName,
      this.businessActivity,
      this.businessRegistrationNumberOrABN, //validates for mandatory only, other validations are in saveAndClose()
      this.registeredAddress,
      this.isApplyingForEMDG,
      this.relationship,
      this.otherRelatedCompany)
  }

  saveAndClose = (callback?: { (res: boolean): any }) => {
    if (this.state.loading) {
      callback && callback(false);
      return;
    }

    this.validate((res) => {
      //res=overall validation result for the supplied fields;
      if (!res) {
        callback && callback(false);
        return; //return early
      } else {
        //overall still valid, continue to additional validations related to ABN/BizRegNumber.
        const selectedCountry = this.registeredAddress.getSelectedCountry(); //OR could have used `this.state.model.registeredAddress?.country`

        if (selectedCountry === 'Australia') {
          //1) client-side validation: ABN-algo format
          const inputABN = this.businessRegistrationNumberOrABN.getValue();
          const isAbnValid = isAbnFormatValid(inputABN);
          if (!isAbnValid) {
            this.businessRegistrationNumberOrABN.setError("ABN invalid")
            callback && callback(false);
            return; //return early
          }

          //2) server-side validation: WebSvc on ABR.
          this.abnAjax.getByAbn(this, inputABN,
            (abnDetails) => {
              const existInABR = abnDetails !== null && abnDetails.abn == inputABN;
              if (existInABR) {
                this.props.onChange(this.state.model, true); //add it to UI-table
              } else {
                this.businessRegistrationNumberOrABN.setError("ABN invalid")
              }
              callback && callback(existInABR); //can be true/false.
            }
            , (_, err) => {
              console.error('Ajax error', err);
              this.businessRegistrationNumberOrABN.setError("Ajax error.")
              callback && callback(false);
            }
          )
        } else {
          //not Australia (or country not even selected yet); No further validation required, anything is accepted/valid.
          this.props.onChange(this.state.model, true);
          callback && callback(true);
        }
      }
    })
    //DONT put any code after this.validate(), it is NOT synchronous; instead put code above inside the callback. 
  }

  componentDidMount() {
    if (this.props.importing) {
      this.validate((res) => {
        const temp = { invalid: !res }
        this.props.onChange({ ...this.state.model, ...temp }, true)
      })
    } else if (this.state.model.invalid) {
      this.validate(() => { })
    }
  }

  onChange = (change: Partial<RelatedCompanyModel>, callback?: { (): any }) => {
    this.setState({
      model: { ...this.state.model, ...change },
    }, callback)
  }

  isSameAcnAsApplicant(acn: string): boolean {
    // return acn && this.props.application.acn && this.props.application.a.replace(/\s/g, '') == acn.replace(/\s/g, ''); //TODO SG
    return false
  }

  render() {
    const relationshipDataSource = [
      { value: CompanyRelationship.Parentcompany, label: 'Parent company' },
      { value: CompanyRelationship.Subsidiarycompany, label: 'Subsidiary company' },
      { value: CompanyRelationship.Subsidiarywithsameparentcompany, label: 'Subsidiary with same parent company' },
      { value: CompanyRelationship.Shareholder, label: 'Shareholder' }]

    const { model } = this.state
    return (
      <>
        <TextField
          ref={(r) => this.companyName = r}
          id={`${this.props.id}-companyName`}
          label="Company name"
          value={model.companyName}
          onChange={(companyName) => this.onChange({ companyName })}
          required
          maxLength={200}
        />
        <TextField
          ref={(r) => this.businessActivity = r}
          id={`${this.props.id}-businessActivity`}
          label="Nature of business activity"
          value={model.businessActivity}
          onChange={(businessActivity) => this.onChange({ businessActivity })}
          required
          maxLength={200}
        />

        <AbnOrAnyField
          ref={(r) => { this.businessRegistrationNumberOrABN = r }}
          id={`${this.props.id}-bizRegNumberOrAbn`}
          label="ABN / Business Registration Number"
          value={model.businessRegistrationNumberOrABN}
          onChange={(bizRegNumberOrAbn) => this.onChange({ businessRegistrationNumberOrABN: bizRegNumberOrAbn.replace(/ /g, '') })}
          maxLength={20}

          required={model.registeredAddress?.country === 'Australia' }
          selectedCountry={model.registeredAddress?.country}
        />

        <AddressField
          ref={(r) => this.registeredAddress = r}
          id={`${this.props.id}-registeredAddress`}
          label="Registered address"
          value={model.registeredAddress}
          onChange={(registeredAddress) => this.onChange({ registeredAddress })}
          australianAddressesOnly={false}
          required
          countries={this.props.addressCountries}
        />

        <TextSelectField
          ref={(r) => this.relationship = r}
          id={`${this.props.id}-relationship`}
          label="Relationship"
          value={model.relationship}
          onChange={(relationship) => this.onChange({ relationship })}
          dataSource={relationshipDataSource}
          required
        />
        <BooleanRadioButtonField
          ref={(r) => this.isApplyingForEMDG = r}
          id={`${this.props.id}-isApplyingForEMDG`}
          label="Is this company applying for EMDG?"
          value={model.isApplyingForEMDG}
          onChange={(isApplyingForEMDG) => this.onChange({ isApplyingForEMDG })}
          dataSource={yesNoDataSource}
          required
        />
        <ShowIf condition={!this.props.preview} className="clearfix mt-3">
                <Button className="emdg-btn float-right" onClick={(e) => this.saveAndClose()}>Complete row</Button>
                <Button className="emdg-btn emdg-btn--secondary float-left mr-2" onClick={this.props.onClose}>Cancel</Button>
                <Button className="emdg-btn emdg-btn--secondary float-left" onClick={this.props.onRemove} style={{ display: this.props.model ? '' : 'none' }}>Remove</Button>
        </ShowIf>

        <Backdrop on={this.state.loading} />
      </>
    )
  }
}
